// frontend/src/utils/docApiResolver.js
// Purpose: Provide the *actual* documents API paths used by the backend.
// Notes:
//   • Your backend mounts documents under:  /api/documents
//   • List endpoint lives at "/" (needs trailing slash to avoid 307).
//   • Upload is POST /api/documents/upload (multipart).
//   • Flags update is PATCH /api/documents/{id} (per-doc; no bulk route).
//   • Auth token should be injected by axiosInstance (do NOT hardcode tokens here).
//
// Updated: 2025-09-18

// We keep axiosInstance import even if not used here so future helpers can reuse it.
import axiosInstance from "../api/axiosInstance";

// Single, explicit source of truth for paths.
export const DOC_API = {
  list:   "/api/documents/",                 // GET with params (tenant_id, customer_id?, job_id?, limit?, q?)
  upload: "/api/documents/upload",           // POST multipart/form-data
  patch:  (id) => `/api/documents/${id}`,    // PATCH JSON { portal_visible?, downloadable? }
  health: "/api/documents/health",           // GET for quick diagnostics
};

/**
 * Backwards-compatible resolver. Previously this probed many candidates.
 * Now we return the verified, canonical endpoints immediately.
 */
let __DOC_API_CACHE = null;
let __DOC_API_LOGGED = false;

export async function resolveDocApi() {
  if (!__DOC_API_CACHE) {
    __DOC_API_CACHE = { ...DOC_API };
    if (!__DOC_API_LOGGED) {
      __DOC_API_LOGGED = true;
      // eslint-disable-next-line no-console
      console.info("[docApiResolver] Using canonical document API paths:", __DOC_API_CACHE);
      // Expose for quick console checks
      // @ts-ignore
      window.__docApi = __DOC_API_CACHE;
    }
  }
  return __DOC_API_CACHE;
}

// Optional: tiny helpers (safe no-ops if you want to use them)

/** GET recent/listed documents (wraps axios with correct trailing slash). */
export async function listDocuments(params) {
  return axiosInstance.get(DOC_API.list, { params });
}

/** POST a multipart upload to the verified upload route. */
export async function uploadDocument(formData) {
  return axiosInstance.post(DOC_API.upload, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

/** PATCH per-document flags. */
export async function patchDocumentFlags(id, payload) {
  return axiosInstance.patch(DOC_API.patch(id), payload);
}
